import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { fmtMoney, fmtPct, exportToPDF, exportToExcel } from "../lib/exports";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const startOfWeek = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
};

export default function ReporteSemanal() {
  const [fechaIni, setFechaIni] = useState(startOfWeek());
  const [sucursalId, setSucursalId] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/sucursales").then((r) => setSucursales(r.data)); }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = { fecha_ini: fechaIni };
      if (sucursalId) params.sucursal_id = sucursalId;
      const { data } = await api.get("/reportes/semana", { params });
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const expPDF = () => {
    if (!data) return;
    exportToPDF({
      title: `Reporte Semanal`, subtitle: `${data.fecha_ini} a ${data.fecha_fin}`,
      columns: [
        { header: "Fecha", key: "fecha" }, { header: "Ventas", key: "ventas_fmt" },
        { header: "Costo", key: "costo_fmt" }, { header: "Gastos Op.", key: "gastos_fmt" }, { header: "Utilidad", key: "utilidad_fmt" },
      ],
      rows: data.rows.map((r) => ({ ...r, ventas_fmt: fmtMoney(r.ventas), costo_fmt: fmtMoney(r.costo), gastos_fmt: fmtMoney(r.gastos), utilidad_fmt: fmtMoney(r.utilidad) })),
      summary: [
        { label: "Ventas Totales", value: fmtMoney(data.resumen.ventas) },
        { label: "Utilidad Neta", value: fmtMoney(data.resumen.utilidad_neta) },
        { label: "Margen", value: fmtPct(data.resumen.margen_pct) },
      ],
    });
  };
  const expXLS = () => {
    if (!data) return;
    exportToExcel({ title: "Reporte_Semanal", rows: data.rows });
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Reportes</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Reporte Semanal</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={expPDF}><FileDown className="w-4 h-4 mr-2" />PDF</Button>
          <Button variant="outline" onClick={expXLS}><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>

      <Card className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Inicio de semana</Label>
          <Input type="date" value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} className="mt-1.5 bg-blue-50 border-blue-200" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Sucursal</Label>
          <Select value={sucursalId || "all"} onValueChange={(v) => setSucursalId(v === "all" ? "" : v)}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end"><Button onClick={cargar} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Consultar</Button></div>
      </Card>

      {loading && <div className="text-sm text-slate-500">Cargando…</div>}

      {data && (
        <>
          <Card className="p-5">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="fecha" stroke="#64748b" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: "#1e293b", border: "none", borderRadius: 6, color: "white" }} />
                  <Legend />
                  <Bar dataKey="ventas" fill="#2563eb" name="Ventas" />
                  <Bar dataKey="utilidad" fill="#10b981" name="Utilidad" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 border-b border-slate-200">
                  <tr><Th>Fecha</Th><Th right>Ventas</Th><Th right>Costo</Th><Th right>Gastos Op.</Th><Th right>Utilidad</Th></tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.fecha} className="border-b border-slate-100">
                      <Td>{r.fecha}</Td>
                      <Td right mono>{fmtMoney(r.ventas)}</Td>
                      <Td right mono>{fmtMoney(r.costo)}</Td>
                      <Td right mono>{fmtMoney(r.gastos)}</Td>
                      <Td right mono className={r.utilidad >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>{fmtMoney(r.utilidad)}</Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-900 text-white">
                  <tr>
                    <td className="px-4 py-3 font-semibold">TOTAL</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">{fmtMoney(data.resumen.ventas)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(data.resumen.costo_venta)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(data.resumen.gastos_operativos)}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-lg">{fmtMoney(data.resumen.utilidad_neta)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
const Th = ({ children, right }) => <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) => <td className={`px-4 py-2.5 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>;
